import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { LucideIcon, Info, BarChart3, Star } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  formatHealthNumberSmart,
  formatHealthPercentScore,
} from "@/lib/formatHealthValue";

type MetricKey =
  | "steps"
  | "distance_km"
  | "active_energy_kcal"
  | "resting_heart_rate_bpm"
  | "vo2_max_ml_per_kg_min"
  | "sleep_hours"
  | "flights_climbed"
  | "basal_energy_kcal"
  | "exercise_minutes"
  | "stand_hours"
  | "heart_rate_bpm"
  | "heart_rate_min_bpm"
  | "heart_rate_max_bpm"
  | "heart_rate_avg_bpm"
  | "weight_kg"
  | "body_fat_pct"
  | "height_cm"
  | "bmi"
  | "stress_score"
  | "strain"
  | "hrv_sdnn_ms"
  | "respiratory_rate_per_min";

interface HealthMetricChartProps {
  metric: MetricKey;
  label: string;
  icon: LucideIcon;
  unit: string;
  dbColumn?: string;
  formatter?: (v: number) => number;
  /** When true, show a note instead of nothing when no data */
  showWhenEmpty?: boolean;
  /** Info popup content - shows (i) button when provided */
  infoContent?: string;
  /** Metric key for Analyze - when provided, shows Analyze button that navigates to chat */
  analyzeMetricKey?: MetricKey;
  /** Whether this metric is favorited (for Favorites section) */
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

const DB_COLUMN_MAP: Record<MetricKey, string> = {
  steps: "steps",
  distance_km: "distance_m",
  active_energy_kcal: "active_energy_kcal",
  resting_heart_rate_bpm: "resting_heart_rate_bpm",
  vo2_max_ml_per_kg_min: "vo2_max_ml_per_kg_min",
  sleep_hours: "sleep_hours",
  flights_climbed: "flights_climbed",
  basal_energy_kcal: "basal_energy_kcal",
  exercise_minutes: "exercise_minutes",
  stand_hours: "stand_hours",
  heart_rate_bpm: "heart_rate_bpm",
  heart_rate_min_bpm: "heart_rate_min_bpm",
  heart_rate_max_bpm: "heart_rate_max_bpm",
  heart_rate_avg_bpm: "heart_rate_avg_bpm",
  weight_kg: "weight_kg",
  body_fat_pct: "body_fat_pct",
  height_cm: "height_cm",
  bmi: "bmi",
  stress_score: "stress_score",
  strain: "strain",
  hrv_sdnn_ms: "hrv_sdnn_ms",
  respiratory_rate_per_min: "respiratory_rate_per_min",
};

export const HealthMetricChart = ({
  metric,
  label,
  icon: Icon,
  unit,
  dbColumn,
  formatter,
  showWhenEmpty = false,
  infoContent,
  analyzeMetricKey,
  isFavorite,
  onToggleFavorite,
}: HealthMetricChartProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const col = dbColumn ?? DB_COLUMN_MAP[metric];
  const [data, setData] = useState<
    { date: string; value: number; formattedDate: string }[]
  >([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const startDate = thirtyDaysAgo.toISOString().split("T")[0];

        const { data: rows, error } = await supabase
          .from("health_metrics")
          .select(`metric_date, ${col}`)
          .eq("user_id", user.id)
          .gte("metric_date", startDate)
          .order("metric_date", { ascending: true });

        if (error) {
          console.error(`[HealthMetricChart] Fetch error for ${metric}:`, error);
          setLoading(false);
          return;
        }

        const formatValue = formatter ?? ((v: number) => v);
        const isScore0to100 = metric === "stress_score" || metric === "strain";
        const chartData = ((rows ?? []) as unknown as Record<string, unknown>[])
          .filter((r) => {
            const val = r[col];
            const num = Number(val);
            if (val == null || Number.isNaN(num)) return false;
            if (isScore0to100) return num >= 0 && num <= 100;
            return num >= 0;
          })
          .map((r) => {
            let val = Number(r[col]);
            if (metric === "distance_km" && col === "distance_m") {
              val = val / 1000;
            }
            if (isScore0to100) {
              val = Math.max(0, Math.min(100, val));
            }
            return {
              date: r.metric_date as string,
              value: formatValue(val),
              formattedDate: new Date(r.metric_date as string).toLocaleDateString(
                "en-US",
                { month: "short", day: "numeric" }
              ),
            };
          });

        setData(chartData);
      } catch (e) {
        console.error(`[HealthMetricChart] Error for ${metric}:`, e);
      } finally {
        setLoading(false);
      }
  }, [user?.id, metric, col]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refetch when HealthKit sync completes (app foreground / Health tab)
  useEffect(() => {
    const onSyncComplete = () => fetchData();
    window.addEventListener("health-sync-complete", onSyncComplete);
    return () => window.removeEventListener("health-sync-complete", onSyncComplete);
  }, [fetchData]);

  if (loading) {
    return (
      <Card className="bg-gradient-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-normal text-base">
            <Icon className="h-4 w-4 text-primary" />
            {label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">
            {t('common.loading')}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    if (showWhenEmpty) {
      return (
        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-normal text-base">
              <Icon className="h-4 w-4 text-primary" />
              {label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t('components.charts.healthMetric.noDataAvailable')}
            </p>
          </CardContent>
        </Card>
      );
    }
    return null;
  }

  const CustomTooltip = ({
    active,
    payload,
    label: tooltipLabel,
  }: {
    active?: boolean;
    payload?: { value: number }[];
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    const v = payload[0]?.value ?? 0;
    const displayVal =
      metric === "strain"
        ? formatHealthPercentScore(v)
        : metric === "stress_score"
          ? formatHealthNumberSmart(v)
          : metric === "distance_km" || metric === "bmi"
            ? v.toFixed(2)
            : ["sleep_hours", "vo2_max_ml_per_kg_min", "weight_kg", "body_fat_pct", "hrv_sdnn_ms", "respiratory_rate_per_min"].includes(metric)
              ? formatHealthNumberSmart(v)
              : String(Math.round(v));
    return (
      <div
        className="rounded-lg border-2 border-border p-3 text-sm shadow-lg"
        style={{
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
          opacity: 1,
        }}
      >
        <p className="font-semibold mb-1 text-foreground">{tooltipLabel}</p>
        <p className="text-base font-bold text-primary">
          {displayVal}
          {metric !== "strain" && metric !== "stress_score" ? ` ${unit}` : ""}
        </p>
        {metric === "stress_score" && (
          <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
            {t('components.charts.healthMetric.stressScoreHint')}
          </p>
        )}
      </div>
    );
  };

  return (
    <Card className="bg-gradient-card border-0">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 font-normal text-base">
            <Icon className="h-4 w-4 text-primary" />
            {t('components.charts.healthMetric.titleLast30Days', { label })}
          </CardTitle>
          <div className="flex items-center gap-1">
            {onToggleFavorite && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                aria-label={isFavorite ? t('components.charts.healthMetric.removeFromFavorites') : t('components.charts.healthMetric.addToFavorites')}
              >
                <Star className={`h-4 w-4 ${isFavorite ? "fill-primary text-primary" : ""}`} />
              </Button>
            )}
            {infoContent && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                    <Info className="h-4 w-4" aria-label={t('components.charts.healthMetric.info')} />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="text-base">{label}</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">{infoContent}</p>
                </DialogContent>
              </Dialog>
            )}
            {analyzeMetricKey && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1 text-xs"
                onClick={() => {
                  const metricLogic =
                    analyzeMetricKey === "stress_score"
                      ? `\n\n=== STRAIV STRESS SCORE (READ CAREFULLY) ===\n- This is a numeric score from 0–100. It is NOT a percentage (do not say "percent" or add a % sign).\n- HIGHER score = BETTER for the user (calmer recovery signal, lower physiological stress).\n- LOWER score = a harder day physiologically (poorer recovery signal in the model).\n- When you interpret trends, "improving" for the user means the line going UP over time (if that matches their goals).\n`
                      : analyzeMetricKey === "strain"
                        ? `\n\n=== STRAIV STRAIN (READ CAREFULLY) ===\n- Strain is a 0–100 daily LOAD metric (often displayed with a % in the UI). HIGHER strain = more demanding day (activity, cardio load, etc.).\n- Do NOT confuse with Stress Score: Stress Score higher = better recovery; Strain higher = more load. They are different.\n`
                        : "";
                  const msg = `You are my health coach. Analyze my "${label}" from Apple Health over the last 30 days.${metricLogic}\n1) Explain what this metric measures and why it matters.\n2) Describe the trend (direction, variability) using the data—use the correct "higher/lower is better" rules above.\n3) Give a personalized analysis and concrete, actionable recommendations.\n\nUse my synced health context and tools if needed. If there is no data for this metric, say so clearly and tell me how to start logging or connect Apple Health on iPhone. Be specific and encouraging.`;
                  navigate("/chat", {
                    state: { prefilledMessage: msg, autoSendHealthAnalysis: true },
                  });
                }}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                {t('components.charts.healthMetric.analyze')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 md:px-4">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="formattedDate" tick={{ fontSize: 10 }} />
            <YAxis
              tick={{ fontSize: 10 }}
              width={metric === "stress_score" || metric === "strain" ? 40 : 36}
              domain={metric === "stress_score" || metric === "strain" ? [0, 100] : ["auto", "auto"]}
              tickFormatter={
                metric === "strain"
                  ? (val: number) => `${Math.round(val)}%`
                  : metric === "stress_score"
                    ? (val: number) => String(Math.round(val))
                    : undefined
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
              activeDot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
